import React, { useState } from "react";
import theme from "../../data/theme";

/**
 * SaleEditor — correct or delete any sale, not just the most recent one.
 *
 * Undo reaches the last entry only. Over 192 picks an error noticed three picks
 * later is guaranteed, and at that point the only alternatives are undoing four
 * correct entries or living with a wrong board all night.
 *
 * The PLAYER is not editable here on purpose: a wrong player is a delete and a
 * re-entry through the entry strip, which is one keyboard loop and keeps the
 * resolver as the single way a name gets onto the board.
 */
export default function SaleEditor({ sale, teamNames, onSave, onDelete, onCancel }) {
  const [price, setPrice] = useState(String(sale.price));
  const [team, setTeam] = useState(sale.team);

  const priceNum = Number(price);
  const valid = Number.isFinite(priceNum) && priceNum >= 0;

  const field = {
    background: theme.colors.bg.raised, border: `1px solid ${theme.colors.border.default}`,
    borderRadius: theme.radius.md, color: theme.colors.text.primary, fontFamily: "inherit",
    fontSize: theme.fontSize.sub, padding: "9px 12px", outline: "none", width: "100%",
  };
  const label = {
    fontSize: theme.fontSize.micro, textTransform: "uppercase", letterSpacing: "0.08em",
    color: theme.colors.text.faint, fontWeight: theme.fontWeight.bold, marginBottom: 4, display: "block",
  };

  return (
    <div
      data-testid="sale-editor"
      style={{
        position: "fixed", inset: 0, background: theme.helpers.overlay(0.7), zIndex: 200,
        display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape") onCancel();
        if (e.key === "Enter" && valid) onSave({ price: Math.round(priceNum), team });
      }}
    >
      <div style={{
        background: theme.colors.extended.modalBg, border: `1px solid ${theme.colors.border.default}`,
        borderRadius: theme.radius.xl, padding: 24, width: 380, maxWidth: "100%",
      }}>
        <div style={{
          fontSize: theme.fontSize.heading, fontWeight: theme.fontWeight.heavy,
          color: theme.colors.text.primary, marginBottom: 4,
        }}>{sale.name}</div>
        <div style={{ fontSize: theme.fontSize.caption, color: theme.colors.text.faint, marginBottom: 18 }}>
          {sale.pos}{sale.posRank ? ` · ${sale.posRank}` : " · not on our board"}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "110px 1fr", gap: 12, marginBottom: 20 }}>
          <div>
            <label style={label} htmlFor="se-price">Price</label>
            <input id="se-price" style={{ ...field, textAlign: "center" }} value={price} inputMode="numeric"
              autoFocus onChange={e => setPrice(e.target.value.replace(/[^0-9]/g, ""))} />
          </div>
          <div>
            <label style={label} htmlFor="se-team">Team</label>
            <select id="se-team" style={{ ...field, cursor: "pointer" }} value={team}
              onChange={e => setTeam(Number(e.target.value))}>
              {teamNames.map((n, i) => <option key={i} value={i}>{n}</option>)}
            </select>
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <button onClick={onDelete} style={{
            background: "transparent", border: `1px solid ${theme.colors.status.danger}`,
            borderRadius: theme.radius.md, color: "#fca5a5", fontFamily: "inherit",
            fontSize: theme.fontSize.body, padding: "9px 14px", cursor: "pointer",
          }}>Delete</button>
          <button onClick={onCancel} style={{
            marginLeft: "auto", background: theme.colors.bg.raised,
            border: `1px solid ${theme.colors.border.default}`, borderRadius: theme.radius.md,
            color: theme.colors.text.primary, fontFamily: "inherit", fontSize: theme.fontSize.body,
            padding: "9px 16px", cursor: "pointer",
          }}>Cancel</button>
          <button onClick={() => valid && onSave({ price: Math.round(priceNum), team })} disabled={!valid} style={{
            background: valid ? theme.colors.brand.gradientBtn : theme.colors.bg.raised,
            border: "none", borderRadius: theme.radius.md, color: valid ? "#fff" : theme.colors.text.faint,
            fontFamily: "inherit", fontSize: theme.fontSize.body, fontWeight: theme.fontWeight.bold,
            padding: "9px 16px", cursor: valid ? "pointer" : "default",
          }}>Save</button>
        </div>
      </div>
    </div>
  );
}
